import React, { useState, useEffect } from "react";
import supabase from "../../../../utils/supabase";
import useUserStore from "../../../../stores/user";
import { getTimeFromNow } from "../../../../utils/time";
import { ReportsPagination } from "./ReportsPagination";
import { toast } from "react-toastify";

// Helper function to get file extension from MIME type
function getFileExtension(mimeType: string): string {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/jpeg":
      return "jpg";
    case "application/pdf":
      return "pdf";
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    default:
      return "txt"; // Default to .txt if MIME type is not recognized
  }
}

async function downloadResource(
  e: React.MouseEvent<HTMLAnchorElement>
): Promise<void> {
  try {
    e.preventDefault();
    // Get the URL of the resource from the 'href' attribute of the clicked element
    if (!e.currentTarget) return;
    const resourceURL = (e.currentTarget as HTMLAnchorElement).href;
    const blobResource = await (await fetch(resourceURL)).blob();

    // Generate a unique URL for the Blob object
    const url = window.URL.createObjectURL(blobResource);

    const newLink = document.createElement("a");
    newLink.href = url;

    // Determine the file extension based on the MIME type
    const fileExtension = getFileExtension(blobResource.type);
    newLink.download = `downloadedResource.${fileExtension}`;

    document.body.appendChild(newLink);
    newLink.click();

    // Remove the link from the document body
    document.body.removeChild(newLink);

    // Release the object URL to free up memory
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    toast.error("Error downloading resource");
  }
}

interface Report {
  id: string;
  name: string;
  fileType: string;
  reportUrl: string;
  created_at: string;
}

const ReportsTable = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [limit] = useState(5);
  const { user } = useUserStore();

  useEffect(() => {
    if (!user?.id) return;
    fetchReports(true);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchReports(false);
  }, [currentPage]);

  const fetchReports = async (isInitialLoad: boolean = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }

      // Calculate offset for pagination
      const from = (currentPage - 1) * limit;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from("analyticsreports")
        .select("*", { count: "exact" })
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) {
        console.error("Error fetching reports:", error);
        return;
      }

      setReports(data || []);
      setTotalReports(count || 0);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setPageLoading(false);
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalReports / limit);

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6 p-5 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Recent Reports</h3>
      </div>

      {loading ? (
        <div className="min-h-[300px] flex flex-col">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">Report Name</th>
                  <th className="px-6 py-3 text-left">Generated</th>
                  <th className="px-6 py-3 text-left">Format</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse">
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : reports.length === 0 ? (
        <div className="min-h-[300px] flex items-center justify-center text-center py-8 text-gray-500">
          <div>
            <p>No reports generated yet.</p>
            <p className="text-sm">
              Use the Export button above to generate your first report.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="min-h-[300px] overflow-x-auto relative">
            {pageLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <span className="text-sm text-gray-600">Loading...</span>
                </div>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">Report Name</th>
                  <th className="px-6 py-3 text-left">Generated</th>
                  <th className="px-6 py-3 text-left">Format</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reports.map((report) => (
                  <tr key={report.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {report.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {getTimeFromNow(report.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 inline-flex text-xs rounded-full font-semibold ${
                          report.fileType === "PDF"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {report.fileType}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={report.reportUrl}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                          downloadResource(e)
                        }
                        className="text-indigo-600 hover:text-indigo-900 mr-3 cursor-pointer"
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalReports > limit && (
            <ReportsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalReports={totalReports}
              limit={limit}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ReportsTable;
